import { useState, useEffect, useMemo } from 'react';
import { Search, ShieldCheck, ShieldX, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { getAllAnalyzedSongs, setSongVerdict, SongReview } from '../../services/songReviews';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

type VerdictFilter = 'all' | 'passed' | 'blocked' | 'unreviewed';
type SortField = 'reviewedAt' | 'title' | 'artist' | 'profanity';

export default function SongLibraryPage() {
  const { user } = useAuth();
  const [songs, setSongs] = useState<SongReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [sortField, setSortField] = useState<SortField>('reviewedAt');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllAnalyzedSongs();
        setSongs(data);
      } catch {
        toast.error('Failed to load song library');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSetVerdict = async (song: SongReview, verdict: 'passed' | 'blocked') => {
    if (!user?.uid) return;
    const newVerdict = song.djVerdict === verdict ? undefined : verdict;
    try {
      await setSongVerdict(song.title, song.artist, newVerdict || null, user.uid);
      setSongs(prev => prev.map(s =>
        s.id === song.id
          ? { ...s, djVerdict: newVerdict, djReviewedAt: newVerdict ? new Date().toISOString() : undefined, djReviewedBy: newVerdict ? user.uid : undefined }
          : s
      ));
      toast.success(newVerdict === 'passed' ? 'Marked as passed' : newVerdict === 'blocked' ? 'Marked as blocked' : 'Verdict cleared');
    } catch {
      toast.error('Failed to update verdict');
    }
  };

  const profanityOrder = { none: 0, mild: 1, moderate: 2, heavy: 3 };

  const filteredSongs = useMemo(() => {
    let result = [...songs];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(q) || s.artist.toLowerCase().includes(q)
      );
    }

    // Verdict filter
    if (verdictFilter === 'passed') result = result.filter(s => s.djVerdict === 'passed');
    else if (verdictFilter === 'blocked') result = result.filter(s => s.djVerdict === 'blocked');
    else if (verdictFilter === 'unreviewed') result = result.filter(s => !s.djVerdict);

    // Sort
    result.sort((a, b) => {
      if (sortField === 'reviewedAt') {
        const aDate = a.djReviewedAt || a.analyzedAt || '';
        const bDate = b.djReviewedAt || b.analyzedAt || '';
        return bDate.localeCompare(aDate); // Most recent first
      }
      if (sortField === 'title') return a.title.localeCompare(b.title);
      if (sortField === 'artist') return a.artist.localeCompare(b.artist);
      if (sortField === 'profanity') return profanityOrder[b.profanityLevel] - profanityOrder[a.profanityLevel];
      return 0;
    });

    return result;
  }, [songs, searchQuery, verdictFilter, sortField]);

  const stats = useMemo(() => ({
    total: songs.length,
    passed: songs.filter(s => s.djVerdict === 'passed').length,
    blocked: songs.filter(s => s.djVerdict === 'blocked').length,
    unreviewed: songs.filter(s => !s.djVerdict).length,
  }), [songs]);

  const getProfanityBadge = (level: string) => {
    switch (level) {
      case 'none': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'mild': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'moderate': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'heavy': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getProfanityLabel = (level: string) => {
    if (level === 'none') return 'Clean';
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-500" />
          Song Library
        </h1>
        <div className="text-center py-12 text-gray-500">Loading song library...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-purple-500" />
        Song Library
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        {stats.total} songs analyzed &middot; {stats.passed} passed &middot; {stats.blocked} blocked &middot; {stats.unreviewed} unreviewed
      </p>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title or artist..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        {/* Verdict filter tabs */}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {(['all', 'passed', 'blocked', 'unreviewed'] as VerdictFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setVerdictFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                verdictFilter === f
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              {f === 'all' ? `All (${stats.total})` :
               f === 'passed' ? `Passed (${stats.passed})` :
               f === 'blocked' ? `Blocked (${stats.blocked})` :
               `Unreviewed (${stats.unreviewed})`}
            </button>
          ))}
        </div>

        {/* Sort */}
        <select
          value={sortField}
          onChange={e => setSortField(e.target.value as SortField)}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
        >
          <option value="reviewedAt">Most Recent</option>
          <option value="title">Title</option>
          <option value="artist">Artist</option>
          <option value="profanity">Profanity Level</option>
        </select>
      </div>

      {/* Song List */}
      {filteredSongs.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {songs.length === 0 ? 'No songs have been analyzed yet. Use the AI analysis button on any song to get started.' : 'No songs match your filters.'}
        </div>
      ) : (
        <div className="space-y-1">
          {filteredSongs.map(song => (
            <div key={song.id} className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${
              song.djVerdict === 'blocked' ? 'border-l-4 border-l-red-500' :
              song.djVerdict === 'passed' ? 'border-l-4 border-l-green-500' : ''
            }`}>
              {/* Song row */}
              <div
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setExpandedId(expandedId === song.id ? null : song.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white text-sm truncate flex items-center gap-1.5">
                    <span className="truncate">{song.title}</span>
                    {song.hasProfanity && (
                      <span className="explicit-badge flex-shrink-0 text-xs">E</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{song.artist}</p>
                </div>

                {/* Profanity badge */}
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getProfanityBadge(song.profanityLevel)}`}>
                  {getProfanityLabel(song.profanityLevel)}
                </span>

                {/* Verdict badge */}
                {song.djVerdict === 'passed' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 flex-shrink-0">
                    <ShieldCheck className="w-3 h-3" /> Passed
                  </span>
                ) : song.djVerdict === 'blocked' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 flex-shrink-0">
                    <ShieldX className="w-3 h-3" /> Blocked
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 flex-shrink-0">
                    Unreviewed
                  </span>
                )}

                {/* Expand chevron */}
                {expandedId === song.id ? (
                  <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </div>

              {/* Expanded details */}
              {expandedId === song.id && (
                <div className="px-3 pb-3 pt-0 border-t border-gray-100 dark:border-gray-700">
                  <div className="space-y-3 mt-3">
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                      <p className="text-sm text-gray-900 dark:text-white">{song.summary}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Profanity Analysis</h4>
                      <p className="text-sm text-gray-900 dark:text-white">{song.profanityAnalysis}</p>
                    </div>
                    {song.djReviewedAt && (
                      <p className="text-xs text-gray-400">
                        Reviewed {new Date(song.djReviewedAt).toLocaleDateString()}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSetVerdict(song, 'passed'); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm transition-colors ${
                          song.djVerdict === 'passed'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                        }`}
                      >
                        <ShieldCheck className="w-4 h-4" />
                        Pass
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSetVerdict(song, 'blocked'); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm transition-colors ${
                          song.djVerdict === 'blocked'
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                        }`}
                      >
                        <ShieldX className="w-4 h-4" />
                        Block
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
